import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Globe, MapPin, Truck, Plane } from 'lucide-react';
import { cn } from '@/lib/utils';

interface CoverageRegionsCardProps {
  regions?: string[] | null;
  serviceRadius?: number | null;
  className?: string;
}

// Map region codes to display labels and icons
const regionConfig: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
  'local': { label: 'Local Area', icon: <MapPin className="w-3 h-3" />, color: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
  'regional': { label: 'Regional', icon: <Truck className="w-3 h-3" />, color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  'national': { label: 'Nationwide', icon: <Globe className="w-3 h-3" />, color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
  'europe': { label: 'Europe', icon: <Globe className="w-3 h-3" />, color: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400' },
  'north_america': { label: 'North America', icon: <Globe className="w-3 h-3" />, color: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400' },
  'asia_pacific': { label: 'Asia Pacific', icon: <Globe className="w-3 h-3" />, color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  'middle_east': { label: 'Middle East', icon: <Globe className="w-3 h-3" />, color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
  'worldwide': { label: 'Worldwide', icon: <Plane className="w-3 h-3" />, color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' },
  'global': { label: 'Global Coverage', icon: <Plane className="w-3 h-3" />, color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' },
};

const getRegionDisplay = (region: string): { label: string; icon: React.ReactNode; color: string } => {
  const config = regionConfig[region.toLowerCase().replace(/[\s-]/g, '_')];
  if (config) return config;
  
  return {
    label: region,
    icon: <Globe className="w-3 h-3" />,
    color: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
  };
};

export const CoverageRegionsCard: React.FC<CoverageRegionsCardProps> = ({
  regions,
  serviceRadius,
  className
}) => {
  if ((!regions || regions.length === 0) && !serviceRadius) return null;

  return (
    <Card className={cn("border-indigo-200/60 dark:border-indigo-700/40 bg-gradient-to-br from-indigo-50/50 to-violet-50/30 dark:from-indigo-900/20 dark:to-violet-900/10", className)}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold flex items-center gap-2 text-indigo-700 dark:text-indigo-400">
          <Globe className="w-4 h-4" />
          Coverage Area
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Service Radius */}
        {serviceRadius && (
          <div className="flex items-center gap-3 p-2.5 bg-white/60 dark:bg-black/20 rounded-lg">
            <div className="p-2 rounded-full bg-indigo-100 dark:bg-indigo-900/40">
              <MapPin className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Service Radius</p>
              <p className="font-bold text-lg text-indigo-700 dark:text-indigo-300">
                {serviceRadius} <span className="text-xs font-normal">km</span>
              </p>
            </div>
          </div>
        )}

        {/* Coverage Regions */}
        {regions && regions.length > 0 && (
          <div>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-2">Service Regions</p>
            <div className="flex flex-wrap gap-1.5">
              {regions.map((region, idx) => {
                const display = getRegionDisplay(region);
                return (
                  <Badge 
                    key={idx} 
                    variant="outline" 
                    className={cn("gap-1.5 py-1", display.color)}
                  >
                    {display.icon}
                    {display.label}
                  </Badge>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CoverageRegionsCard;
